import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MembershipPrompt from "./MembershipPrompt";
import SearchResults from "./SearchResults";
import { useCredits } from "../contexts/CreditContext";
import { GAUtils } from "../model/GAUtils";
import { logSearch } from "../utils/searchLogger";
import { auth } from "../firebaseConfig";

interface GoogleCustomSearchProps {
    language?: string;
}

const GoogleCustomSearch: React.FC<GoogleCustomSearchProps> = ({ language = "en" }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [insufficientCredits, setInsufficientCredits] = useState(false);
    const cseElementRef = useRef<HTMLDivElement>(null);
    const initializedRef = useRef(false);
    const lastSearchQueryRef = useRef<string>("");
    const navigate = useNavigate();
    const location = useLocation();
    const { credits, hasCredits, consumeCredit, loading: creditsLoading } = useCredits();

    // Check if Google CSE script is loaded
    const isScriptLoaded = useCallback(() => {
        return typeof (window as any).google !== 'undefined' && 
               typeof (window as any).google.search !== 'undefined' &&
               typeof (window as any).google.search.cse !== 'undefined';
    }, []);

    // Check if CSE element is rendered
    const isCseRendered = useCallback(() => {
        return document.getElementById("___gcse_0") !== null;
    }, []);

    // Trigger CSE to process hash parameters (for pagination and back navigation)
    const triggerCseHashNavigation = useCallback(() => {
        if (isScriptLoaded() && isCseRendered()) {
            try {
                const gcse = (window as any).google.search.cse;
                // If there's a hash with gsc parameters, trigger CSE to process it
                if (window.location.hash && window.location.hash.includes('gsc.')) {
                    // Multiple methods to trigger CSE hash processing
                    if (gcse && gcse.element) {
                        // Method 1: Use go() to process hash
                        if (typeof gcse.element.go === 'function') {
                            gcse.element.go();
                        }
                        // Method 2: Try to trigger a search with hash parameters
                        const hashParams = new URLSearchParams(window.location.hash.substring(1));
                        const query = hashParams.get('gsc.q');
                        if (query && typeof gcse.element.execute === 'function') {
                            // This might trigger the search with the hash parameters
                            setTimeout(() => {
                                gcse.element.execute(query);
                            }, 100);
                        }
                    }
                }
            } catch (err) {
                console.warn("Error triggering CSE hash navigation:", err);
            }
        }
    }, [isScriptLoaded, isCseRendered]);

    // Handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            // Re-trigger CSE when user navigates back/forward
            if (initializedRef.current && isCseRendered()) {
                setLoading(true);
                setTimeout(() => {
                    triggerCseHashNavigation();
                    setLoading(false);
                }, 100);
            }
        };

        window.addEventListener('popstate', handlePopState);
        
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isCseRendered, triggerCseHashNavigation]);

    // Reset and reinitialize when location changes (for SPA navigation)
    useEffect(() => {
        if (initializedRef.current && isCseRendered()) {
            // CSE already loaded, just trigger hash navigation without loading
            triggerCseHashNavigation();
        }
    }, [location.key, isCseRendered, triggerCseHashNavigation]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let checkInterval: NodeJS.Timeout;
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds at 100ms intervals

        // Manually trigger CSE initialization
        const initializeCse = () => {
            if (!cseElementRef.current) {
                return false;
            } else {
                setLoading(false);
            }
            try {
                // Check if script is loaded
                if (isScriptLoaded()) {
                    // Check if element already has CSE rendered
                    if (isCseRendered()) {
                        initializedRef.current = true;
                        setLoading(false);
                        setError(false);
                        return true;
                    }

                    // Use Google CSE's callback system to ensure initialization
                    const element = cseElementRef.current;
                    
                    // Set up __gcse callback if not already set
                    if (!(window as any).__gcse) {
                        (window as any).__gcse = {};
                    }
                    
                    // Force Google CSE to re-scan for elements
                    // This is needed for SPAs where elements are added after script loads
                    const gcse = (window as any).google.search.cse;
                    if (gcse && gcse.element) {
                        // Try to trigger element rendering
                        try {
                            // Method 1: Use element.go() if available
                            if (typeof gcse.element.go === 'function') {
                                gcse.element.go();
                            }
                            
                            // Method 2: Trigger parse if available
                            if (typeof gcse.element.parse === 'function') {
                                gcse.element.parse(element);
                            }
                            
                            // Method 3: Force re-initialization by temporarily hiding/showing
                            const originalDisplay = element.style.display;
                            element.style.display = 'none';
                            setTimeout(() => {
                                element.style.display = originalDisplay || 'block';
                            }, 10);
                        } catch (err) {
                            console.warn("CSE initialization method failed:", err);
                        }
                    }
                }
            } catch (err) {
                console.error("Error initializing CSE:", err);
            }
            
            return false;
        };

        // Start checking immediately
        const startChecking = () => {
            // Initial check - if already rendered from previous visit
            if (isCseRendered()) {
                initializedRef.current = true;
                // CSE already rendered, no need to show loading
                setLoading(false);
                setError(false);
                // If there's a hash with CSE parameters, trigger CSE to process it
                if (window.location.hash && window.location.hash.includes('gsc.')) {
                    triggerCseHashNavigation();
                }
                return;
            }

            // Try to initialize if script is loaded
            if (isScriptLoaded()) {
                initializeCse();
            }

            // Poll for CSE to render
            checkInterval = setInterval(() => {
                attempts++;

                // Check if already rendered
                if (isCseRendered()) {
                    initializedRef.current = true;
                    clearInterval(checkInterval);
                    if (timeoutId) clearTimeout(timeoutId);
                    
                    // Brief delay before hiding loading for smoother transition
                    setTimeout(() => {
                        setLoading(false);
                        setError(false);
                        // Trigger hash navigation if hash exists
                        triggerCseHashNavigation();
                    }, 100);
                    return;
                }

                // Try to initialize every 5 attempts if script is loaded
                if (isScriptLoaded() && attempts % 5 === 0) {
                    initializeCse();
                }

                // Give up after max attempts
                if (attempts >= maxAttempts) {
                    console.warn("CSE failed to load after", maxAttempts * 100, "ms");
                    setLoading(false);
                    setError(true);
                    clearInterval(checkInterval);
                    if (timeoutId) clearTimeout(timeoutId);
                }
            }, 100);
        };

        // Only show loading if CSE is not already rendered
        if (!isCseRendered()) {
            setLoading(true);
        }
        setError(false);

        // Wait a tiny bit for DOM to be ready, then start
        const initDelay = setTimeout(() => {
            startChecking();
        }, 50);

        // Timeout after 10 seconds
        timeoutId = setTimeout(() => {
            if (!isCseRendered()) {
                console.warn("CSE loading timeout after 10 seconds");
                setLoading(false);
                setError(true);
                if (checkInterval) clearInterval(checkInterval);
            }
        }, 10000);

        return () => {
            clearTimeout(initDelay);
            if (checkInterval) clearInterval(checkInterval);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [language, isScriptLoaded, isCseRendered, triggerCseHashNavigation]);

    // Handle credit consumption when search is performed
    const handleSearchStart = useCallback(async (query: string) => {
        // Skip if same query (user might be paginating or re-rendering)
        if (query === lastSearchQueryRef.current) {
            // Track pagination event
            GAUtils.trackSearchPagination(query, 1);
            return true;
        }

        // Track search attempt
        GAUtils.trackSearch(query, credits);

        // Check if user has credits (allow while loading - backend will validate)
        if (!hasCredits && !creditsLoading) {
            setInsufficientCredits(true);
            // Track out of credits
            GAUtils.trackOutOfCredits('search');
            return false;
        }

        try {
            const result = await consumeCredit(query);
            if (result && result.success) {
                lastSearchQueryRef.current = query;
                setInsufficientCredits(false);
                // Log search term to Firestore (fire and forget)
                logSearch(query, auth.currentUser?.uid);
                return true;
            } else {
                setInsufficientCredits(true);
                // Track out of credits
                GAUtils.trackOutOfCredits('search_consume_failed');
                return false;
            }
        } catch (err: any) {
            console.error("Error consuming credit:", err);
            setInsufficientCredits(true);
            // Track search error
            GAUtils.trackSearchError(query, err.message || 'Credit consumption failed');
            return false;
        }
    }, [hasCredits, consumeCredit, credits, creditsLoading]);

    // Set up CSE callback to intercept searches
    useEffect(() => {
        // Define the callback that CSE will call before searching
        const originalCallback = (window as any).__gcse?.searchCallbacks?.web?.starting;
        
        (window as any).__gcse = (window as any).__gcse || {};
        (window as any).__gcse.searchCallbacks = (window as any).__gcse.searchCallbacks || {};
        (window as any).__gcse.searchCallbacks.web = (window as any).__gcse.searchCallbacks.web || {};
        
        // Store reference to our handler for the CSE
        (window as any).__gcse.searchCallbacks.web.starting = (gname: string, query: string) => {
            // We can't easily block the search from here, but we track it
            // The credit check happens via URL hash monitoring below
            if (originalCallback) {
                originalCallback(gname, query);
            }
        };

        return () => {
            if ((window as any).__gcse?.searchCallbacks?.web) {
                (window as any).__gcse.searchCallbacks.web.starting = originalCallback;
            }
        };
    }, []);

    // Listen for hash changes to handle pagination and credit consumption
    useEffect(() => {
        const isCseRendered = () => {
            return document.getElementById("___gcse_0") !== null;
        };

        const handleHashChange = async () => {
            // When hash changes (e.g., search or pagination), check credits
            if (initializedRef.current && isCseRendered()) {
                // Parse the hash to get the query
                const hash = window.location.hash;
                if (hash && hash.includes('gsc.q=')) {
                    const params = new URLSearchParams(hash.substring(1));
                    const query = params.get('gsc.q');
                    
                    if (query && query !== lastSearchQueryRef.current) {
                        // New search query - consume credit
                        const canSearch = await handleSearchStart(query);
                        
                        if (!canSearch) {
                            // Redirect to balance page if no credits
                            navigate('/balance');
                            return;
                        }
                    }
                }

                setTimeout(() => {
                    try {
                        const gcse = (window as any).google?.search?.cse;
                        if (gcse && gcse.element && typeof gcse.element.go === 'function') {
                            gcse.element.go();
                        }
                    } catch (err) {
                        console.warn("Error handling hash change:", err);
                    }
                }, 100);
            }
        };

        window.addEventListener('hashchange', handleHashChange);
        
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [handleSearchStart, navigate]);

    // Use CSS-based hiding instead of conditional rendering to avoid React DOM conflicts
    // with Google CSE's direct DOM manipulation
    return (
        <div>
            {/* Loading state - always render, hide with CSS */}
            <div 
                className="text-center p-4 text-gray-600 animate-pulse"
                style={{ display: loading && !error ? 'block' : 'none' }}
            >
                Loading search...
            </div>

            {/* Error state - always render, hide with CSS */}
            <div 
                className="text-center p-4 text-gray-600"
                style={{ display: error ? 'block' : 'none' }}
            >
                <p className="mb-2">Unable to load search component.</p>
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        GAUtils.trackButtonClick('refresh_page', 'search_error');
                        window.location.reload();
                    }}
                    className="text-blue-500 underline hover:text-blue-700"
                >
                    Refresh the page
                </button>
            </div>

            {/* Insufficient credits warning - always render, hide with CSS */}
            <div style={{
                display: insufficientCredits ? 'block' : 'none',
                background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                border: '1px solid #fed7aa',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                textAlign: 'center'
            }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💰</div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#c2410c', fontSize: '1.1rem', fontWeight: 600 }}>
                    Out of Credits
                </h3>
                <p style={{ margin: '0 0 1rem 0', color: '#9a3412', fontSize: '0.95rem' }}>
                    You have {credits} credits remaining. Each search costs 1 credit.
                </p>
                <button
                    onClick={() => {
                        GAUtils.trackButtonClick('purchase_credits', 'insufficient_credits_warning');
                        navigate('/balance');
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}
                >
                    Purchase Credits
                </button>
            </div>

            {/* CSE element - must be in DOM for Google script to find it */}
            <div
                ref={cseElementRef}
                className="gcse-search"
                aria-label="Google Custom Search"
                data-num="500"
                style={{ 
                    display: error ? 'none' : 'block',
                    minHeight: loading ? '60px' : 'auto'
                }}
            ></div>

            {/* Always render - they return null internally when not needed */}
            <MembershipPrompt />
            <SearchResults />
        </div>
    );
};

export default GoogleCustomSearch;